import { classifyScannedCode, isRunSheetText } from '@barso/shared';
import { checkPairing } from '../native';

/**
 * Kamera-Scan (POS-02, INT-01): erkennt QR-Code und Code 128 des Etiketts. Bevorzugt die eingebaute Erkennung des Browsers
 * (`BarcodeDetector`, Chrome auf Android); iOS-Safari hat sie nicht, dort lädt die Kasse bei Bedarf ZXing nach. Beides
 * liefert nur Text: Prüfen, Ablegen und Warnen macht dieselbe Logik wie bei der Eingabe per Hand oder Handscanner.
 */

interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
interface BarcodeDetectorConstructor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
  getSupportedFormats?(): Promise<string[]>;
}

/**
 * Verhindert, dass dasselbe Etikett im Kamerabild fortlaufend mehrfach erfasst wird. Gleitend: Jede Sichtung setzt die
 * Zeit neu, ein Code gilt erst als neu, wenn er `holdMs` lang nicht mehr zu sehen war. Ein liegen gebliebenes Etikett
 * wird so nie ein zweites Mal gemeldet (Dauerscan), ein erneut vorgehaltenes nach kurzer Lücke schon.
 */
export class ScanGate {
  /** Zuletzt gesehen je Code: zwei Codes im selben Bild (Etikett und Herstellercode) heben die Sperre nicht auf. */
  private readonly seen = new Map<string, number>();

  /**
   * `sliding = false`: Zeit nur beim Annehmen setzen (Eingabefeld: feste Sperrzeit statt Lücke im Bild).
   * `kind = 'any'`: jeder nicht leere Text gilt (z. B. Kopplungs-Link), `labelOrRunSheet`: Etikettencodes und der Text eines
   * Laufzettels (nur an der Kasse), sonst nur Etikettencodes. Ein Laufzettel-Text kommt im Modus `label` nie durch.
   */
  constructor(
    private readonly holdMs: number = 1_500,
    private readonly sliding: boolean = true,
    private readonly kind: ScanKind = 'label',
  ) {}

  /**
   * Gibt den Code zurück, wenn er neu ist: BARSO-Code (Leerzeichen und Bindestriche werden entfernt) oder übernommener
   * fremder Code (bei `kind = 'any'` der Text ohne Rand-Leerzeichen), und nicht innerhalb von `holdMs` schon gesehen.
   * Danach zählt er wieder als neuer Scan.
   */
  accept(text: string, nowMs: number): string | null {
    const code =
      this.kind === 'any'
        ? text.trim() === ''
          ? null
          : text.trim()
        : this.kind === 'labelOrRunSheet' && isRunSheetText(text)
          ? text.trim()
          : (classifyScannedCode(text)?.code ?? null);
    if (code === null) return null;
    const last = this.seen.get(code);
    const fresh = last === undefined || nowMs - last >= this.holdMs;
    if (fresh || this.sliding) this.seen.set(code, nowMs);
    if (this.seen.size > 50) {
      for (const [key, at] of this.seen) if (nowMs - at >= this.holdMs) this.seen.delete(key);
    }
    return fresh ? code : null;
  }

  /**
   * Nach einer Pause der Kamera (Seite verdeckt): Was bis `fromMs` noch gesperrt war, gilt als bis `toMs` gesehen. Ein
   * Etikett, das während der Pause liegen blieb, wird nach dem Neustart so nicht ein zweites Mal gemeldet.
   */
  carryOver(fromMs: number, toMs: number): void {
    for (const [code, at] of this.seen) if (fromMs - at < this.holdMs) this.seen.set(code, toMs);
  }
}

/**
 * Was die Kamera meldet: `label` nur Etikettencodes (`classifyScannedCode`, alles andere ist Rauschen im Bild),
 * `labelOrRunSheet` zusätzlich den Text eines Laufzettels (Plan Welle 3): **nur an der Kasse**, Rückgabe, Prüfen, Annahme und
 * Vorab-Erfassung bleiben bei `label` und bekommen nie einen Laufzettel-Text. `any` meldet jeden Text, etwa den Kopplungs-QR
 * einer Kasse (die Seite prüft ihn selbst).
 */
export type ScanKind = 'label' | 'labelOrRunSheet' | 'any';

/** Modi, die Etikettencodes melden (alles andere im Bild ist Rauschen oder bekommt eine Rückmeldung). */
const isLabelKind = (kind: ScanKind): boolean => kind === 'label' || kind === 'labelOrRunSheet';

/** Fähigkeiten der laufenden Kamera. Zoom und Licht gibt es nur, wo das Gerät sie meldet (Safari kennt sie meist nicht). */
export interface CameraCapabilities {
  zoom?: { min: number; max: number };
  torch: boolean;
  /** `null`: das Gerät meldet seine Fokus-Modi nicht. */
  focusContinuous: boolean | null;
  /** Anzahl der wählbaren Rückkameras (ohne Ultraweitwinkel), für „Kamera wechseln“. */
  cameras: number;
}

/** Messwerte für das Diagnose-Overlay. Enthält nie Codes, Texte oder Personendaten. */
export interface CameraInfo {
  detector: 'detector' | 'zxing';
  width: number;
  height: number;
  /** Erkennungsversuche je Sekunde (gleitend über 2 s). */
  fps: number;
  focusModes: string[] | null;
  zoom: number | null;
  zoomRange: { min: number; max: number } | null;
  torch: boolean;
  camera: string | null;
}

/** Warum ein erkannter Text kein Etikett ist: nie der Text selbst (er kann ein Token enthalten). */
export type IgnoredKind = 'pairing' | 'runSheet' | 'url' | 'other';

export interface CameraOptions {
  /** Im Modus `label`: nicht leerer Text, der kein Code ist (höchstens alle 4 s). */
  onIgnored?: (kind: IgnoredKind) => void;
  /** 6 s ohne Treffer, einmal je Lücke. */
  onIdle?: () => void;
  /** Irgendein Text im Bild gesehen (auch ein schon gemeldeter): beendet die Anzeige „kein Code“. */
  onSighting?: () => void;
  /** Sperre für wiederholte Codes, damit ein Neustart (Seite wieder sichtbar) nichts doppelt meldet. */
  gate?: ScanGate;
  /** Neustart nach einer Pause: Zeitpunkt des Stoppens, die Sperre übernimmt die damals gesehenen Codes (`carryOver`). */
  resumeFrom?: number;
}

export interface CameraSession {
  stop: () => void;
  /**
   * Pausiert die Meldung neuer Codes (Warnung, Bezahlen). Die Erkennung läuft weiter und hält die Sperre aktuell:
   * Was beim Fortsetzen noch im Bild liegt, wird nicht nachträglich gemeldet.
   */
  setPaused: (paused: boolean) => void;
  /** Aktueller Stand, ändert sich nach `switchCamera`. */
  readonly capabilities: CameraCapabilities;
  /** Aktueller Zoom oder `null` ohne Zoom. */
  readonly zoom: number | null;
  /** Licht an? Ein Kamerawechsel schaltet es aus. */
  readonly torch: boolean;
  setZoom: (value: number) => Promise<void>;
  setTorch: (on: boolean) => Promise<void>;
  switchCamera: () => Promise<void>;
  info: () => CameraInfo;
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** Pause ohne Treffer, nach der ein Tipp erscheint. */
export const IDLE_HINT_MS = 6_000;
/** Mindestabstand zwischen zwei Rückmeldungen „fremder Inhalt“. */
const IGNORED_GAP_MS = 4_000;

const STORAGE_CAMERA = 'barso.camera';
const STORAGE_ZOOM = 'barso.camera.zoom';

/**
 * Auflösung je Art: ein QR auf dem Bildschirm braucht Pixel (1920), Etiketten die Bildrate (1280 × 720). Der Laufzettel-QR
 * ist dicht (bis 500 Zeichen) und braucht beides.
 */
export function videoConstraints(kind: ScanKind): MediaTrackConstraints {
  if (kind === 'labelOrRunSheet') {
    return { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } };
  }
  return kind === 'any'
    ? { width: { ideal: 1920 }, height: { ideal: 1080 } }
    : { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } };
}

/** Formate der eingebauten Erkennung (`BarcodeDetector`): beim Koppeln nur QR. */
export function detectorFormats(kind: ScanKind): string[] {
  return kind === 'any'
    ? ['qr_code']
    : ['qr_code', 'code_128', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_39', 'code_93', 'itf'];
}

function readStore(key: string): Record<string, unknown> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? '{}');
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function writeStore(key: string, value: Record<string, unknown>): void {
  try {
    if (Object.keys(value).length === 0) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Speicher gesperrt (privates Fenster): die Wahl gilt nur für diese Sitzung
  }
}

const FRONT = /front|user|selfie|vorder|facing front/i;
// Nur Ultraweitwinkel: Die virtuellen iPhone-Kameras („Back Dual Wide Camera“) schalten für die Nähe selbst um und bleiben
const WIDE = /ultra[\s-]?wide|ultraweit|0[.,]5\s*x/i;

/** Wählbare Rückkameras: ohne Frontkamera, ohne Ultraweitwinkel (nur wenn es sonst nichts gibt). */
export function rearCameras(devices: readonly MediaDeviceInfo[]): MediaDeviceInfo[] {
  const inputs = devices.filter((device) => device.kind === 'videoinput');
  const rear = inputs.filter((device) => !FRONT.test(device.label));
  const base = rear.length > 0 ? rear : inputs;
  const narrow = base.filter((device) => !WIDE.test(device.label));
  return narrow.length > 0 ? narrow : base;
}

function trackCapabilities(track: MediaStreamTrack | undefined): Record<string, unknown> {
  try {
    return (track?.getCapabilities() ?? {}) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function trackSettings(track: MediaStreamTrack | undefined): MediaTrackSettings {
  try {
    return track?.getSettings() ?? {};
  } catch {
    return {};
  }
}

function focusModesOf(caps: Record<string, unknown>): string[] | null {
  const modes = caps.focusMode;
  return Array.isArray(modes)
    ? modes.filter((mode): mode is string => typeof mode === 'string')
    : null;
}

function zoomRangeOf(caps: Record<string, unknown>): { min: number; max: number } | null {
  const zoom = caps.zoom as { min?: unknown; max?: unknown } | undefined;
  if (typeof zoom?.min !== 'number' || typeof zoom.max !== 'number' || zoom.max <= zoom.min)
    return null;
  return { min: zoom.min, max: zoom.max };
}

async function applyAdvanced(
  track: MediaStreamTrack | undefined,
  set: Record<string, unknown>,
): Promise<boolean> {
  try {
    if (track === undefined) return false;
    await track.applyConstraints({ advanced: [set] });
    return true;
  } catch {
    return false;
  }
}

function ignoredKind(text: string): IgnoredKind {
  if (checkPairing(text).ok) return 'pairing';
  if (isRunSheetText(text)) return 'runSheet';
  try {
    const url = new URL(text);
    if (url.protocol === 'http:' || url.protocol === 'https:') return 'url';
  } catch {
    // kein Link
  }
  return 'other';
}

/** Gibt es überhaupt eine Kamera-API (sicherer Kontext, Browser-Unterstützung)? */
export function cameraAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'mediaDevices' in navigator &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  );
}

/**
 * Startet die Kamera im `video`-Element und ruft `onCode` für jeden neuen Code auf. `stop` beendet Kamera und Erkennung,
 * `setPaused` hält die Meldungen an.
 * Wirft, wenn die Kamera nicht zugelassen ist oder fehlt (die Oberfläche zeigt dann die Eingabe per Hand).
 */
export async function startCamera(
  video: HTMLVideoElement,
  onCode: (code: string) => void,
  kind: ScanKind = 'label',
  options: CameraOptions = {},
): Promise<CameraSession> {
  if (!cameraAvailable()) throw new Error('Dieses Gerät oder dieser Browser bietet keine Kamera.');
  const media = navigator.mediaDevices;
  // Der Zoom der Kasse bleibt derselbe, ob sie Laufzettel liest oder nicht
  const zoomKey = kind === 'labelOrRunSheet' ? 'label' : kind;

  /** Genau diese Kamera (nur beim Wechseln, `exact`): Fehlt sie, wirft der Browser statt still eine andere zu nehmen. */
  const openExact = (deviceId: string): Promise<MediaStream> =>
    media.getUserMedia({
      video: { ...videoConstraints(kind), deviceId: { exact: deviceId } },
      audio: false,
    });
  /** Rückkamera, auf Wunsch eine bestimmte (`ideal`): Ist sie weg, nimmt der Browser die Standard-Rückkamera. */
  const openPreferred = (deviceId?: string): Promise<MediaStream> =>
    media.getUserMedia({
      video: {
        ...videoConstraints(kind),
        facingMode: { ideal: 'environment' },
        ...(deviceId === undefined ? {} : { deviceId: { ideal: deviceId } }),
      },
      audio: false,
    });
  const stopStream = (target: MediaStream): void => {
    for (const track of target.getTracks()) track.stop();
  };

  const remembered = readStore(STORAGE_CAMERA).deviceId;
  let stream = await openPreferred(typeof remembered === 'string' ? remembered : undefined);

  let rear: MediaDeviceInfo[] = [];
  const refreshDevices = async (): Promise<void> => {
    try {
      rear = rearCameras(await media.enumerateDevices());
    } catch {
      rear = [];
    }
  };
  await refreshDevices();
  if (typeof remembered === 'string' && !rear.some((device) => device.deviceId === remembered)) {
    writeStore(STORAGE_CAMERA, {}); // gemerkte Kamera gibt es nicht mehr
  }

  const currentId = (): string | undefined => trackSettings(stream.getVideoTracks()[0]).deviceId;
  /**
   * Wechselt zur Kamera `deviceId`. Die laufende wird vorher geschlossen: Viele Androids öffnen nur eine Kamera zugleich,
   * der Wechsel schlüge sonst immer fehl. Klappt die neue nicht, öffnet sie die vorige wieder (bzw. die Standard-Rückkamera);
   * scheitert auch das, wirft sie.
   */
  const switchTo = async (deviceId: string): Promise<boolean> => {
    const previous = currentId();
    stopStream(stream);
    try {
      stream = await openExact(deviceId);
      return true;
    } catch {
      stream = await openPreferred(previous);
      return false;
    }
  };

  // Einmal automatisch: Rückkamera ohne Dauer-Fokus (Fixfokus, Ultraweit) gegen die nächste tauschen. Eine gemerkte,
  // bewusst gewählte Kamera bleibt.
  if (typeof remembered !== 'string' && rear.length > 1) {
    const track = stream.getVideoTracks()[0];
    const modes = focusModesOf(trackCapabilities(track));
    const label = track?.label ?? '';
    const poor = (modes !== null && !modes.includes('continuous')) || WIDE.test(label);
    const other = rear.find((device) => device.deviceId !== currentId());
    if (poor && other !== undefined && (await switchTo(other.deviceId))) {
      writeStore(STORAGE_CAMERA, { deviceId: other.deviceId });
    }
  }

  const caps = (): Record<string, unknown> => trackCapabilities(stream.getVideoTracks()[0]);
  let zoomNow: number | null = null;
  let torchOn = false;

  /** Dauer-Fokus und gemerkter bzw. Standard-Zoom für die laufende Kamera. */
  const tune = async (): Promise<void> => {
    const track = stream.getVideoTracks()[0];
    const capabilities = caps();
    const modes = focusModesOf(capabilities);
    if (modes === null || modes.includes('continuous')) {
      await applyAdvanced(track, { focusMode: 'continuous' });
    }
    const range = zoomRangeOf(capabilities);
    zoomNow = null;
    if (range !== null) {
      const saved = readStore(STORAGE_ZOOM)[zoomKey];
      const wish = typeof saved === 'number' ? saved : kind === 'any' ? 2 : 1;
      const value = Math.min(Math.max(wish, range.min), range.max);
      if (await applyAdvanced(track, { zoom: value })) zoomNow = value;
    }
  };
  await tune();

  video.srcObject = stream;
  video.setAttribute('playsinline', 'true');
  await video.play();

  const gate = options.gate ?? new ScanGate(1_500, true, kind);
  if (options.resumeFrom !== undefined) gate.carryOver(options.resumeFrom, Date.now());
  let stopped = false;
  let paused = false;
  // Funktion statt Feldzugriff: Nach dem Start hielte TypeScript den Wert in der Schleife sonst für fest false
  const isStopped = (): boolean => stopped;
  let stopLibrary: (() => void) | null = null;
  let switching = false;
  let way: 'detector' | 'zxing' = 'zxing';
  /** Zeitpunkte der Erkennungsversuche der letzten 2 s (für Versuche/s); begrenzt, auch wenn niemand `info()` liest. */
  const attempts: number[] = [];
  const noteAttempt = (): void => {
    const now = Date.now();
    attempts.push(now);
    while (attempts.length > 0 && now - (attempts[0] ?? now) > 2_000) attempts.shift();
  };
  let lastIgnored = -Infinity;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;

  const armIdle = (): void => {
    if (idleTimer !== null) clearTimeout(idleTimer);
    if (options.onIdle === undefined) return;
    idleTimer = setTimeout(() => {
      idleTimer = null;
      if (isStopped()) return;
      if (paused || document.hidden) {
        armIdle(); // nur ein sichtbares, laufendes Bild zählt als Lücke
        return;
      }
      options.onIdle?.();
    }, IDLE_HINT_MS);
  };
  armIdle();

  const handle = (text: string): void => {
    if (text.trim() === '') return;
    armIdle();
    options.onSighting?.();
    const code = gate.accept(text, Date.now());
    if (code !== null) {
      if (!isStopped() && !paused) onCode(code);
      return;
    }
    // Fremder Inhalt im Modus „label“ (z. B. ein Kopplungs-QR an der Kasse, ein Laufzettel an einer Station): Rückmeldung,
    // nie der Text selbst
    if (
      isLabelKind(kind) &&
      !paused &&
      classifyScannedCode(text) === null &&
      !(kind === 'labelOrRunSheet' && isRunSheetText(text))
    ) {
      const now = Date.now();
      if (now - lastIgnored >= IGNORED_GAP_MS) {
        lastIgnored = now;
        options.onIgnored?.(ignoredKind(text.trim()));
      }
    }
  };

  /** Mindestabstand zweier Erkennungen: 80 ms beim Koppeln, 100 ms im Dauerscan (vorher 120 ms). */
  const scanGap = kind === 'any' ? 80 : 100;

  const startZxing = async (): Promise<void> => {
    const { BrowserMultiFormatReader } = await import('@zxing/browser');
    const { BarcodeFormat, DecodeHintType } = await import('@zxing/library');
    const formats =
      kind === 'any'
        ? [BarcodeFormat.QR_CODE]
        : [
            BarcodeFormat.QR_CODE,
            BarcodeFormat.CODE_128,
            BarcodeFormat.EAN_13,
            BarcodeFormat.EAN_8,
            BarcodeFormat.UPC_A,
            BarcodeFormat.UPC_E,
            BarcodeFormat.CODE_39,
            BarcodeFormat.CODE_93,
            BarcodeFormat.ITF,
          ];
    // TRY_HARDER nur bei Einzel-Scans (Koppeln): Bei Strichcodes prüft ZXing damit ein Vielfaches an Zeilen und dreht das
    // Bild, alles im Haupt-Thread. An der Kasse (Dauerscan, iOS) würde das die Bedienung bremsen.
    const hints = new Map<number, unknown>([[DecodeHintType.POSSIBLE_FORMATS, formats]]);
    if (kind === 'any') hints.set(DecodeHintType.TRY_HARDER, true);
    const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: scanGap });
    const controls = await reader.decodeFromStream(stream, video, (result) => {
      if (document.hidden) return;
      noteAttempt();
      if (result !== undefined) handle(result.getText());
    });
    // Inzwischen gestoppt (Wechsel, verdeckte Seite): die Schleife sofort beenden, sonst liefe sie ohne Bild weiter
    if (isStopped()) {
      controls.stop();
      return;
    }
    stopLibrary = () => {
      controls.stop();
    };
  };

  const Detector = (globalThis as { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;
  const supported = Detector === undefined ? [] : ((await Detector.getSupportedFormats?.()) ?? []);
  const wanted = detectorFormats(kind).filter((format) => supported.includes(format));
  const needed = kind === 'any' ? ['qr_code'] : ['qr_code', 'code_128'];
  if (Detector !== undefined && needed.every((format) => wanted.includes(format))) {
    way = 'detector';
    const detector = new Detector({ formats: wanted });
    void (async () => {
      while (!isStopped()) {
        if (document.hidden) {
          await sleep(250); // verdeckte Seite: kein Akku verbrauchen
          continue;
        }
        const began = Date.now();
        try {
          const found = await detector.detect(video);
          noteAttempt();
          for (const barcode of found) handle(barcode.rawValue);
        } catch {
          // einzelnes Bild nicht auswertbar: weiter
        }
        await sleep(Math.max(0, scanGap - (Date.now() - began)));
        if (typeof video.requestVideoFrameCallback === 'function') {
          // Auf das nächste echte Bild warten statt dasselbe noch einmal auszuwerten. Höchstens gut ein Bild lang (40 ms):
          // Liefert der Browser keine Bild-Rückmeldung (Sucher aus dem Sichtfeld gescrollt), bremst das den Takt kaum.
          let frame = 0;
          await Promise.race([
            new Promise<void>((resolve) => {
              frame = video.requestVideoFrameCallback(() => {
                resolve();
              });
            }),
            sleep(40),
          ]);
          video.cancelVideoFrameCallback(frame);
        }
      }
    })();
  } else {
    // Ohne eingebaute Erkennung (iOS-Safari): ZXing, erst jetzt geladen
    await startZxing();
  }

  const session: CameraSession = {
    setPaused: (value) => {
      paused = value;
    },
    get capabilities() {
      const capabilities = caps();
      const range = zoomRangeOf(capabilities);
      const modes = focusModesOf(capabilities);
      return {
        ...(range === null ? {} : { zoom: range }),
        torch: capabilities.torch === true,
        focusContinuous: modes === null ? null : modes.includes('continuous'),
        cameras: rear.length,
      };
    },
    get zoom() {
      return zoomNow;
    },
    get torch() {
      return torchOn;
    },
    setZoom: async (value) => {
      const range = zoomRangeOf(caps());
      if (range === null) return; // ohne Zoom-Fähigkeit nichts anwenden
      const next = Math.min(Math.max(value, range.min), range.max);
      if (await applyAdvanced(stream.getVideoTracks()[0], { zoom: next })) {
        zoomNow = next;
        writeStore(STORAGE_ZOOM, { ...readStore(STORAGE_ZOOM), [zoomKey]: next });
      }
    },
    setTorch: async (on) => {
      if (caps().torch !== true) return;
      if (await applyAdvanced(stream.getVideoTracks()[0], { torch: on })) torchOn = on;
    },
    switchCamera: async () => {
      if (switching || isStopped() || rear.length < 2) return;
      const at = rear.findIndex((device) => device.deviceId === currentId());
      const next = rear[(at + 1) % rear.length];
      if (next === undefined) return;
      switching = true;
      try {
        // ZXing schließt beim Stoppen auch den Strom; der Wechsel öffnet ohnehin einen neuen
        stopLibrary?.();
        stopLibrary = null;
        torchOn = false;
        const done = await switchTo(next.deviceId);
        // Während des Wechsels gestoppt (Seite verdeckt, Sucher geschlossen): die neue Kamera nicht offen lassen
        if (isStopped()) {
          stopStream(stream);
          return;
        }
        if (done) writeStore(STORAGE_CAMERA, { deviceId: next.deviceId });
        await refreshDevices();
        await tune();
        if (isStopped()) {
          stopStream(stream);
          return;
        }
        video.srcObject = stream;
        await video.play();
        if (!isStopped() && way === 'zxing') await startZxing();
      } finally {
        switching = false;
      }
    },
    info: () => {
      const now = Date.now();
      while (attempts.length > 0 && now - (attempts[0] ?? now) > 2_000) attempts.shift();
      const track = stream.getVideoTracks()[0];
      const capabilities = caps();
      return {
        detector: way,
        width: video.videoWidth,
        height: video.videoHeight,
        fps: Math.round((attempts.length / 2) * 10) / 10,
        focusModes: focusModesOf(capabilities),
        zoom: zoomNow,
        zoomRange: zoomRangeOf(capabilities),
        torch: capabilities.torch === true,
        camera: track?.label === undefined || track.label === '' ? null : track.label,
      };
    },
    stop: () => {
      stopped = true;
      if (idleTimer !== null) clearTimeout(idleTimer);
      stopLibrary?.();
      // Licht aus: das Stoppen der Spur schaltet es ebenfalls, ausdrücklich ist es sicherer
      if (torchOn) void applyAdvanced(stream.getVideoTracks()[0], { torch: false });
      stopStream(stream);
      video.srcObject = null;
    },
  };
  return session;
}
